import { Box, Typography, IconButton, CircularProgress } from '@mui/material'
import { type ReactNode, memo, useMemo } from 'react'
import DoneIcon from '@mui/icons-material/Done'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import { SONG_STATUS_BLOCK_STRINGS } from '../strings'

type SongStatus = 'approved' | 'rejected' | 'counteroffer' | null

interface SongStatusBlockProps {
  status: SongStatus
  onChangeStatus: (id: number) => void
  songId: number
  initialLoading: boolean
  isReseting: boolean
  processingSongId?: number | null
}

interface StatusConfig {
  icon: ReactNode
  text: string
  color: string
  iconSize?: 'small' | 'medium' | 'large'
}

const STATUS_CONFIGS: Record<NonNullable<SongStatus>, StatusConfig> = {
  approved: {
    icon: <DoneIcon />,
    text: SONG_STATUS_BLOCK_STRINGS.approved,
    color: 'success.light',
  },
  rejected: {
    icon: <CloseIcon />,
    text: SONG_STATUS_BLOCK_STRINGS.rejected,
    color: 'error.light',
  },
  counteroffer: {
    icon: <DoneIcon />,
    text: SONG_STATUS_BLOCK_STRINGS.counteroffer,
    color: 'success.light',
    iconSize: 'small',
  },
}

const StatusIcon = memo(({ icon, color }: { icon: ReactNode; color: string }) => (
  <Box sx={{ color }}>{icon}</Box>
))

const StatusText = memo(
  ({ text, color, fontWeight = 400 }: { text: string; color: string; fontWeight?: number }) => (
    <Typography color={color} fontWeight={fontWeight} fontSize={16}>
      {text}
    </Typography>
  ),
)

const StatusButton = memo(
  ({
    onClick,
    disabled,
    isReseting,
    iconSize = 'medium',
  }: {
    onClick: () => void
    disabled: boolean
    isReseting: boolean
    iconSize?: 'small' | 'medium' | 'large'
  }) => (
    <IconButton
      sx={{ color: 'text.primary' }}
      size={iconSize}
      onClick={onClick}
      disabled={disabled}
    >
      {isReseting ? <CircularProgress size={24} /> : <EditIcon />}
    </IconButton>
  ),
)

const StatusDisplay = memo(
  ({
    config,
    onReset,
    isDisabled,
    isReseting,
  }: {
    config: StatusConfig
    onReset: () => void
    isDisabled: boolean
    isReseting: boolean
  }) => (
    <Box display="flex" alignItems="center" gap={2} minHeight={56}>
      <Box display="flex" alignItems="center" gap={1}>
        <StatusIcon icon={config.icon} color={config.color} />
        <StatusText
          text={config.text}
          color={config.color}
          fontWeight={config.color === 'error.light' ? 500 : 400}
        />
      </Box>
      <StatusButton
        onClick={onReset}
        disabled={isDisabled}
        isReseting={isReseting}
        iconSize={config.iconSize || 'medium'}
      />
    </Box>
  ),
)

export const SongStatusBlock = memo(
  ({
    status,
    onChangeStatus,
    songId,
    initialLoading,
    isReseting,
    processingSongId,
  }: SongStatusBlockProps) => {
    const statusConfig = useMemo(() => {
      if (!status) return null
      return STATUS_CONFIGS[status]
    }, [status])

    const isOtherSongProcessing = processingSongId !== null && processingSongId !== songId
    const isDisabled = Boolean(initialLoading) || isOtherSongProcessing

    if (!statusConfig) {
      return null
    }

    return (
      <StatusDisplay
        config={statusConfig}
        onReset={() => onChangeStatus(songId)}
        isDisabled={isDisabled}
        isReseting={isReseting}
      />
    )
  },
)
